<template>
  <div>
    <Survey v-if="survey" :survey="survey" />
  </div>
</template>

<script>
import 'survey-core/defaultV2.min.css';
import { Model } from 'survey-core';
import { Survey } from 'survey-vue-ui';
import { ref, computed, getCurrentInstance, watch, onMounted } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import darkTheme from '@/assets/surveyThemes/survey_theme_dark.json';
import lightTheme from '@/assets/surveyThemes/survey_theme_light.json';
import router from '@/router';

const themes = {
  light: lightTheme,
  dark: darkTheme
};

export default {
  components: {
    Survey
  },
  setup() {
    const { skin } = useAppConfig();
    const surveySkin = computed(() => skin.value);

    const id = router.currentRoute.params.id;
    const item = ref(null);
    const survey = ref(null);

    const { proxy } = getCurrentInstance();

    // This will hold the description to show
    const ratingDescription = ref('');
    let descs = {};

    // Helper to get the description for a given question and rating value
    function getDescriptionForRating(question, value) {
      if (!value) return 'Please hover over the rating choices below for descriptions.';
      // Find the page (competency) and question (subcompetency) titles
      const page = survey.value.getPageByQuestion(question);
      const competency = page?.navigationTitle || page?.title;
      const subcompetency = question.title;
      // Find the text label for the selected value
      const rateObj = question.rateValues.find(opt => opt.value.toString() === value.toString());
      const ratingText = rateObj ? rateObj.text : value;
      // Return the description if available
      return (
        descs?.[competency]?.[subcompetency]?.[ratingText] ||
        descs?.[competency]?.[subcompetency]?.[value] ||
        ''
      );
    }

    proxy.$http.get(`/questionnaires/${id}`).then((res) => {
      item.value = res.data;
      const category = item.value.category;
      proxy.$http.get(`/constants/rating-descriptions/${category}`).then((descRes) => {
        descs = descRes.data;
      });

      const surveyJSON = item.value.body;
      surveyJSON['width'] = 1200; // Set width for the questionnaire
      survey.value = new Model(surveyJSON);
      survey.value.data = item.value.answers;

      // Quick hack to fix the text elements in the survey not scrollable
      surveyJSON.pages.forEach(page => {
        page.elements.forEach(element => {
          if (element.type === 'text') {
            element.type = 'comment';
          }
        });
      });

      // Attach listeners to rating items after each question is rendered (modern SurveyJS theme)
      survey.value.onAfterRenderQuestion.add((surveyInstance, options) => {
        if (options.question.getType() === 'rating') {
          // Find all rating item labels in the question
          const items = options.htmlElement.querySelectorAll('.sd-rating__item');
          items.forEach((itemEl, idx) => {
            // Remove any previous listeners/tooltips
            itemEl.onmouseenter = null;
            itemEl.onmouseleave = null;
            itemEl.onclick = null;
            // Remove any previous tooltip
            let tooltip = itemEl.querySelector('.custom-tooltip');
            if (tooltip) itemEl.removeChild(tooltip);
            // Get the value for this rating item
            const input = itemEl.querySelector('input[type=radio]');
            let value = input ? input.value : null;

            // Create tooltip element
            tooltip = document.createElement('div');
            tooltip.className = 'custom-tooltip';
            tooltip.style.display = 'none';
            tooltip.style.position = 'fixed'; // Use fixed to avoid parent overflow
            tooltip.style.zIndex = 1000;
            tooltip.style.background = 'rgba(30, 30, 40, 0.98)';
            tooltip.style.color = '#fff';
            tooltip.style.padding = '20px 22px';
            tooltip.style.borderRadius = '10px';
            tooltip.style.fontSize = '1.01em';
            tooltip.style.whiteSpace = 'pre-line';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.maxWidth = '600px';
            tooltip.style.minWidth = '320px';
            tooltip.style.boxShadow = '0 8px 32px rgba(0,0,0,0.28)';
            tooltip.style.lineHeight = '1.7';
            tooltip.style.textAlign = 'left';
            tooltip.style.wordBreak = 'break-word';
            tooltip.style.transition = 'opacity 0.22s cubic-bezier(.4,0,.2,1)';
            tooltip.style.opacity = '0';
            tooltip.style.overflowY = 'auto';
            tooltip.style.maxHeight = '320px';
            tooltip.style.border = '1.5px solid #444';
            tooltip.style.left = '0'; // Default, will be set on hover
            tooltip.style.top = '0';
            itemEl.appendChild(tooltip);

            // Mouse events for tooltip
            itemEl.onmouseenter = (e) => {
              console.log('HOVERED', value, options.question.title, itemEl);
              const desc = getDescriptionForRating(options.question, value) || '';
              console.log('DESC', desc, 'FOR VALUE', value, 'IN QUESTION', options.question.title);
              if (desc) {
                tooltip.textContent = desc;
                tooltip.style.display = 'block';
                tooltip.style.opacity = '1';
                // Position tooltip centered above or below, but clamp to viewport
                const rect = itemEl.getBoundingClientRect();
                const tooltipWidth = Math.min(tooltip.offsetWidth || 600, 600);
                const tooltipHeight = tooltip.offsetHeight || 60;
                let left = rect.left + rect.width / 2 - tooltipWidth / 2;
                left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
                let top;
                if (rect.top > tooltipHeight + 20) {
                  top = rect.top - tooltipHeight - 14;
                } else {
                  top = rect.bottom + 10;
                }
                // Clamp top to viewport
                top = Math.max(8, Math.min(top, window.innerHeight - tooltipHeight - 8));
                tooltip.style.left = left + 'px';
                tooltip.style.top = top + 'px';
              } else {
                tooltip.style.display = 'none';
                tooltip.style.opacity = '0';
              }
            };
            itemEl.onmouseleave = () => {
              console.log('LEFT', value, options.question.title, itemEl);
              tooltip.style.opacity = '0';
              setTimeout(() => { tooltip.style.display = 'none'; }, 180);
            };
            itemEl.onclick = () => {
              const desc = getDescriptionForRating(options.question, value) || '';
              // Optionally, you can keep the tooltip visible on click or do nothing
            };
          });
        }
      });
      
      survey.value.applyTheme(themes[surveySkin.value]);
      survey.value.mode = "display";

      // Update question.description dynamically on value change
      survey.value.onValueChanged.add((sender, options) => {
        const question = sender.getQuestionByName(options.name);
        if (question && question.getType() === 'rating') {
          const desc = getDescriptionForRating(question, options.value);
          question.description = desc || '';
        }
      });
    });

    watch(surveySkin, (newSkin) => {
      if (survey.value) {
        survey.value.applyTheme(themes[newSkin]);
      }
    });

    return { surveySkin, survey, ratingDescription };
  }
};
</script>

<style scoped>
/* Make SurveyJS question box and rating items wider */
.sd-question,
.sv_qstn {
  max-width: 1200px !important;
  min-width: 700px !important;
}

.sv-rating .sv-item {
  min-width: 120px !important;
}

.custom-tooltip {
  transition: opacity 0.22s cubic-bezier(.4,0,.2,1);
  opacity: 0;
  pointer-events: none;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-size: 1.01em;
  background: rgba(30, 30, 40, 0.98);
  color: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.28);
  padding: 20px 22px;
  line-height: 1.7;
  text-align: left;
  word-break: break-word;
  max-width: 600px;
  min-width: 320px;
  white-space: pre-line;
  overflow-y: auto;
  max-height: 320px;
  border: 1.5px solid #444;
  scrollbar-width: thin;
  scrollbar-color: #888 #222;
  left: 0;
  top: 0;
}
.custom-tooltip::-webkit-scrollbar {
  width: 8px;
  background: #222;
}
.custom-tooltip::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
}

.sv-rating .sv-item {
  position: relative !important;
}
</style>